import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2-v4';

import {
  cssvar, primary, primary800, primary600, primary400, primary200,
  secondary1000, secondary, secondary600, secondary400, secondary200,
} from 'components/new_ui/Colors';
import chartOptions from 'components/utils/chartOptions';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import Select from 'components/new_ui/Select';
import Statistic from 'components/Managers/Dashboard/Views/Statistic';

import EmptyChart from './EmptyChart';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Statistics({ data, variant }) {
  const [subCategory, setSubCategory] = useState();

  const colors = {
    1000: cssvar(variant === 'primary' ? primary : secondary1000),
    800: cssvar(variant === 'primary' ? primary800 : secondary),
    600: cssvar(variant === 'primary' ? primary600 : secondary600),
    400: cssvar(variant === 'primary' ? primary400 : secondary400),
    200: cssvar(variant === 'primary' ? primary200 : secondary200),
  };

  const defaultValue = useMemo(() => data && Object.keys(data)[0], [data]);

  useEffect(() => {
    if (defaultValue) {
      setSubCategory(defaultValue);
    } else {
      setSubCategory(null);
    }
  }, [defaultValue]);

  const statistics = useMemo(() => (
    {
      labels: ['Strength', 'Well Developed', 'Growth Opportunity', 'Development Recommended', 'Blind Spot'],
      datasets: [
        {
          data: subCategory && data ? ([
            data[subCategory].Strength,
            data[subCategory]['Well Developed'],
            data[subCategory]['Growth Opportunity'],
            data[subCategory]['Development Recommended'],
            data[subCategory]['Blind Spot'],
          ]) : [],
          backgroundColor: [colors[1000], colors[800], colors[600], colors[400], colors[200]],
        },
      ],
    }
  ), [subCategory]);

  const total = useMemo(() => (
    (subCategory && data) ? (
      data[subCategory].Strength
        + data[subCategory]['Well Developed']
        + data[subCategory]['Growth Opportunity']
        + data[subCategory]['Development Recommended']
        + data[subCategory]['Blind Spot']
    ) : 0
  ), [subCategory]);

  return (
    <div className="h-100 border border-2 rounded-3 p-3">
      {!data ? <LoadingPlaceholder className="my-5" /> : (
        <>
          <Select
            id={`cpa-${variant}-sub-category`}
            className="mb-2"
            isClearable={false}
            defaultValue={defaultValue ? { label: defaultValue, value: defaultValue } : undefined}
            options={data ? Object.keys(data).map((val) => ({ label: val, value: val })) : undefined}
            onChange={(selectedOption) => setSubCategory(selectedOption.value)}
          />

          {total === 0 ? (
            <EmptyChart colors={colors} />
          ) : (
            <Doughnut data={statistics} options={chartOptions} />
          )}

          <Statistic
            label="Strength"
            value={subCategory && data[subCategory].Strength}
            iconBackgroundColor={colors[1000]}
          />

          <Statistic
            label="Well Developed"
            value={subCategory && data[subCategory]['Well Developed']}
            iconBackgroundColor={colors[800]}
          />

          <Statistic
            label="Growth Opportunity"
            value={subCategory && data[subCategory]['Growth Opportunity']}
            iconBackgroundColor={colors[600]}
          />

          <Statistic
            label="Development Recommended"
            value={subCategory && data[subCategory]['Development Recommended']}
            iconBackgroundColor={colors[400]}
          />

          <Statistic
            label="Blind Spot"
            value={subCategory && data[subCategory]['Blind Spot']}
            iconBackgroundColor={colors[200]}
          />
        </>
      )}
    </div>
  );
}

Statistics.propTypes = {
  data: PropTypes.object,
  variant: PropTypes.string.isRequired,
};

Statistics.defaultProps = {
  data: null,
};
